import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  message, 
  Popconfirm, 
  Row, 
  Col, 
  Card, 
  Input, 
  Select, 
  Space, 
  Typography,
  Tag,
  Modal,
  Form,
  Tooltip,
  Pagination,
  Alert,
  Statistic,
  Badge,
  Avatar
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  SyncOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  GlobalOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import axios from '../utils/axios';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

const FournisseursPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [form] = Form.useForm();
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [selectedUsine, setSelectedUsine] = useState('');

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    nomUsine: ''
  });

  // Options pour les usines avec leurs adresses par défaut
  const usineOptions = [
    { value: 'CIMAF', label: 'CIMAF', adresse: 'THIES, SENEGAL', color: '#1890ff' },
    { value: 'SOCOCIM', label: 'SOCOCIM', adresse: 'DAKAR, SENEGAL', color: '#52c41a' },
    { value: 'DANGOTE', label: 'DANGOTE', adresse: 'THIES, SENEGAL', color: '#fa8c16' },
    { value: 'SAHEL', label: 'SAHEL', adresse: 'THIES, SENEGAL', color: '#722ed1' }
  ];

  // Objet pour les adresses par défaut
  const defaultAdresses = {
    'CIMAF': 'THIES, SENEGAL',
    'SOCOCIM': 'DAKAR, SENEGAL',
    'DANGOTE': 'THIES, SENEGAL',
    'SAHEL': 'THIES, SENEGAL'
  };

  // Objet pour les couleurs des usines
  const usineColors = {
    'CIMAF': '#1890ff',
    'SOCOCIM': '#52c41a',
    'DANGOTE': '#fa8c16',
    'SAHEL': '#722ed1'
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/fournisseurs');
      console.log('📦 Données reçues:', response.data);
      
      if (response.data.success) {
        setData(response.data.data);
        setFilteredData(response.data.data);
        message.success(`${response.data.count} fournisseur(s) chargé(s)`);
      } else {
        message.error('Erreur lors du chargement des fournisseurs');
      }
    } catch (error) {
      console.error('❌ Erreur fetchData:', error);
      message.error('Impossible de charger les fournisseurs. Vérifiez la connexion au serveur.');
      
      // Fallback: données locales pour le développement
      const mockData = [
        { _id: '1', nomUsine: 'CIMAF', adresse: 'THIES, SENEGAL', createdAt: new Date(), updatedAt: new Date() },
        { _id: '2', nomUsine: 'SOCOCIM', adresse: 'DAKAR, SENEGAL', createdAt: new Date(), updatedAt: new Date() }
      ];
      setData(mockData);
      setFilteredData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.nomUsine?.toLowerCase().includes(searchLower) ||
        item.adresse?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.nomUsine) {
      filtered = filtered.filter(item => item.nomUsine === filters.nomUsine);
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    setDuplicateWarning('');
    setSelectedUsine('');
    
    if (record) {
      form.setFieldsValue(record);
      setSelectedUsine(record.nomUsine);
    } else {
      form.resetFields();
      form.setFieldsValue({ adresse: '' });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    setDuplicateWarning('');
    setSelectedUsine('');
    form.resetFields();
  };

  // Vérifier si un doublon existe
  const checkForDuplicate = (nomUsine, adresse) => {
    if (!nomUsine || !adresse) return false;
    
    if (!editingRecord) {
      return data.some(item => 
        item.nomUsine === nomUsine && item.adresse === adresse
      );
    } else {
      return data.some(item => 
        item._id !== editingRecord._id && 
        item.nomUsine === nomUsine && 
        item.adresse === adresse
      );
    }
  };

  // Fonction pour gérer le changement d'usine
  const handleUsineChange = (value) => {
    setSelectedUsine(value);
    
    let newAdresse = '';
    if (defaultAdresses[value]) {
      newAdresse = defaultAdresses[value];
    }
    
    form.setFieldsValue({ adresse: newAdresse });
    
    if (value && newAdresse) {
      const isDuplicate = checkForDuplicate(value, newAdresse);
      if (isDuplicate) {
        setDuplicateWarning(`⚠️ Ce fournisseur existe déjà : ${value} - ${newAdresse}`);
      } else {
        setDuplicateWarning('');
      }
    }
  };

  // Fonction pour gérer le changement d'adresse
  const handleAdresseChange = (e) => {
    const newAdresse = e.target.value;
    const nomUsine = form.getFieldValue('nomUsine');
    
    if (nomUsine && newAdresse) {
      const isDuplicate = checkForDuplicate(nomUsine, newAdresse);
      if (isDuplicate) {
        setDuplicateWarning(`⚠️ Ce fournisseur existe déjà : ${nomUsine} - ${newAdresse}`);
      } else {
        setDuplicateWarning('');
      }
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      if (!values.adresse && values.nomUsine && defaultAdresses[values.nomUsine]) {
        values.adresse = defaultAdresses[values.nomUsine];
      }

      const isDuplicate = checkForDuplicate(values.nomUsine, values.adresse);
      if (isDuplicate) {
        message.error('Ce fournisseur existe déjà !');
        setLoading(false);
        return;
      }

      let response;
      if (editingRecord) {
        response = await axios.put(`/api/fournisseurs/${editingRecord._id}`, values);
        if (response.data.success) {
          const updatedData = data.map(item => 
            item._id === editingRecord._id ? response.data.data : item
          );
          setData(updatedData);
          message.success('Fournisseur modifié avec succès!');
        } else {
          throw new Error(response.data.message);
        }
      } else {
        response = await axios.post('/api/fournisseurs', values);
        if (response.data.success) {
          setData([response.data.data, ...data]);
          message.success('Fournisseur ajouté avec succès!');
        } else {
          throw new Error(response.data.message);
        }
      }
      
      setIsModalVisible(false);
      form.resetFields();
      setEditingRecord(null);
      setDuplicateWarning('');
      setSelectedUsine('');
    } catch (error) {
      console.error('❌ Erreur handleSubmit:', error);
      
      if (error.response?.status === 409) {
        message.error(error.response.data.message || 'Ce fournisseur existe déjà !');
        setDuplicateWarning(error.response.data.message);
      } else {
        message.error(error.response?.data?.message || error.message || 'Erreur lors de l\'opération');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    setLoading(true);
    
    try {
      const response = await axios.delete(`/api/fournisseurs/${record._id}`);
      if (response.data.success) {
        const filtered = data.filter(item => item._id !== record._id);
        setData(filtered);
        message.success('Fournisseur supprimé avec succès!');
      } else {
        message.error(response.data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Erreur handleDelete:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsineOptions = () => {
    if (editingRecord) {
      return usineOptions;
    }
    
    return usineOptions.filter(usine => {
      const existingWithDefaultAdresse = data.some(item => 
        item.nomUsine === usine.value && item.adresse === usine.adresse
      );
      return !existingWithDefaultAdresse;
    });
  };

  const columns = [
    {
      title: 'ID',
      key: 'id',
      width: 70,
      align: 'center',
      render: (_, __, index) => (
        <div className="text-center">
          <Badge 
            count={`F${(currentPage - 1) * pageSize + index + 1}`}
            style={{ 
              backgroundColor: '#f0f2f5',
              color: '#595959',
              fontWeight: 600,
              padding: '0 8px',
              borderRadius: 4
            }}
          />
        </div>
      )
    },
    {
      title: 'USINE',
      dataIndex: 'nomUsine',
      key: 'nomUsine',
      render: (text, record) => (
        <div className="flex items-center">
          <Avatar 
            size="small"
            style={{ 
              backgroundColor: usineColors[text] || '#d9d9d9',
              marginRight: 12
            }}
            icon={<ShopOutlined />}
          />
          <div>
            <Text strong className="block text-sm">{text}</Text>
            <Text type="secondary" className="text-xs">
              Ajouté le {new Date(record.createdAt).toLocaleDateString('fr-FR')}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'ADRESSE',
      dataIndex: 'adresse',
      key: 'adresse',
      render: (text, record) => (
        <div className="flex items-start">
          <EnvironmentOutlined className="text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <Text className="text-sm">{text || defaultAdresses[record.nomUsine] || 'Non spécifiée'}</Text>
            {(!text && record.nomUsine) && (
              <Text type="secondary" className="text-xs block">
                (adresse par défaut)
              </Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'STATUT',
      key: 'statut',
      width: 90,
      align: 'center',
      render: () => (
        <Tag 
          color="success" 
          icon={<CheckCircleOutlined />}
          style={{ 
            margin: 0,
            border: 'none',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: '12px'
          }}
        >
          Actif
        </Tag>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button 
              type="text" 
              size="small"
              icon={<EditOutlined />} 
              onClick={() => showModal(record)}
              className="text-blue-500 hover:text-blue-600"
              style={{ padding: '4px 8px' }}
            />
          </Tooltip>
          <Popconfirm 
            title="Supprimer ce fournisseur ?"
            description="Êtes-vous sûr de vouloir supprimer ce fournisseur ?"
            onConfirm={() => handleDelete(record)}
            okText="Supprimer"
            cancelText="Annuler"
            okType="danger"
          >
            <Tooltip title="Supprimer">
              <Button 
                type="text" 
                size="small"
                danger
                icon={<DeleteOutlined />} 
                style={{ padding: '4px 8px' }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Statistiques
  const stats = {
    total: filteredData.length,
    usinesUniques: new Set(data.map(item => item.nomUsine)).size,
    thiesCount: data.filter(item => item.adresse.includes('THIES')).length,
    dakarCount: data.filter(item => item.adresse.includes('DAKAR')).length,
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Title level={2} className="m-0 font-semibold text-gray-800">
              Fournisseurs de Ciment
            </Title>
            <Text className="text-gray-500 mt-1 block">
              Gestion des fournisseurs de ciment (CIMAF, SOCOCIM, DANGOTE, SAHEL)
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => showModal()}
            className="shadow-sm"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              fontWeight: 500
            }}
          >
            Nouveau Fournisseur
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-8">
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="shadow-sm border-0 hover:shadow-md transition-shadow"
            bodyStyle={{ padding: '20px' }}
          >
            <div className="flex items-center">
              <div className="mr-4 p-3 rounded-lg bg-blue-50">
                <TeamOutlined className="text-blue-600 text-xl" />
              </div>
              <div>
                <Text className="text-gray-500 text-sm">Total Fournisseurs</Text>
                <Statistic 
                  value={stats.total}
                  valueStyle={{ 
                    fontSize: '24px', 
                    fontWeight: 600,
                    color: '#1890ff'
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="shadow-sm border-0 hover:shadow-md transition-shadow"
            bodyStyle={{ padding: '20px' }}
          >
            <div className="flex items-center">
              <div className="mr-4 p-3 rounded-lg bg-green-50">
                <ShopOutlined className="text-green-600 text-xl" />
              </div>
              <div>
                <Text className="text-gray-500 text-sm">Usines Différentes</Text>
                <Statistic 
                  value={stats.usinesUniques}
                  valueStyle={{ 
                    fontSize: '24px', 
                    fontWeight: 600,
                    color: '#52c41a'
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="shadow-sm border-0 hover:shadow-md transition-shadow"
            bodyStyle={{ padding: '20px' }}
          >
            <div className="flex items-center">
              <div className="mr-4 p-3 rounded-lg bg-orange-50">
                <GlobalOutlined className="text-orange-600 text-xl" />
              </div>
              <div>
                <Text className="text-gray-500 text-sm">Fournisseurs à Thiès</Text>
                <Statistic 
                  value={stats.thiesCount}
                  valueStyle={{ 
                    fontSize: '24px', 
                    fontWeight: 600,
                    color: '#fa8c16'
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="shadow-sm border-0 hover:shadow-md transition-shadow"
            bodyStyle={{ padding: '20px' }}
          >
            <div className="flex items-center">
              <div className="mr-4 p-3 rounded-lg bg-purple-50">
                <FileTextOutlined className="text-purple-600 text-xl" />
              </div>
              <div>
                <Text className="text-gray-500 text-sm">Fournisseurs à Dakar</Text>
                <Statistic 
                  value={stats.dakarCount}
                  valueStyle={{ 
                    fontSize: '24px', 
                    fontWeight: 600,
                    color: '#722ed1'
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Card with Filters and Table */}
      <Card 
        className="shadow-sm border-0"
        bodyStyle={{ padding: 0 }}
      >
        {/* Filters Header */}
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <Title level={4} className="m-0 font-semibold">Liste des Fournisseurs</Title>
              <Text type="secondary" className="text-sm">
                {filteredData.length} fournisseur{filteredData.length > 1 ? 's' : ''} trouvé{filteredData.length > 1 ? 's' : ''}
              </Text>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-3">
                <Input
                  placeholder="Rechercher..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  allowClear
                  className="w-full sm:w-64"
                  size="middle"
                />
                
                <Select
                  placeholder="Toutes les usines"
                  value={filters.nomUsine}
                  onChange={(value) => handleFilterChange('nomUsine', value)}
                  allowClear
                  suffixIcon={<FilterOutlined />}
                  className="w-full sm:w-48"
                  size="middle"
                >
                  {usineOptions.map(usine => (
                    <Option key={usine.value} value={usine.value}>
                      <Space>
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: usine.color }}
                        />
                        <span>{usine.label}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </div>
              
              <Button 
                type="text"
                icon={<SyncOutlined spin={loading} />} 
                onClick={fetchData}
                loading={loading}
                className="self-end sm:self-center"
              >
                Actualiser
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="p-1">
          <Table
            columns={columns}
            dataSource={paginatedData}
            rowKey="_id"
            pagination={false}
            loading={loading}
            size="middle"
            className="ant-table-striped"
            rowClassName={(_, index) => index % 2 === 0 ? 'bg-gray-50' : ''}
          />
        </div>

        {/* Pagination Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div>
              <Text type="secondary" className="text-sm">
                Affichage de {Math.min((currentPage - 1) * pageSize + 1, filteredData.length)} à {Math.min(currentPage * pageSize, filteredData.length)} sur {filteredData.length} fournisseurs
              </Text>
            </div>
            
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredData.length}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => `${range[0]}-${range[1]} sur ${total}`}
              pageSizeOptions={['8', '16', '24', '32']}
              size="default"
              className="ant-pagination-custom"
            />
          </div>
        </div>
      </Card>

      {/* Modal pour ajouter/modifier un fournisseur */}
      <Modal
        title={
          <div className="flex items-center">
            <div className="mr-3 p-2 rounded-lg bg-blue-50">
              {editingRecord ? (
                <EditOutlined className="text-blue-600 text-lg" />
              ) : (
                <PlusOutlined className="text-blue-600 text-lg" />
              )}
            </div>
            <div>
              <Text strong className="text-lg block">
                {editingRecord ? "Modifier le Fournisseur" : "Nouveau Fournisseur"}
              </Text>
              <Text type="secondary" className="text-sm">
                {editingRecord ? "Modifiez les informations du fournisseur" : "Ajoutez un nouveau fournisseur de ciment"}
              </Text>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={520}
        centered
        destroyOnClose
        className="modern-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ adresse: '' }}
          className="mt-4"
        >
          {/* Avertissement de doublon */}
          {duplicateWarning && (
            <Alert
              message="Doublon détecté"
              description={duplicateWarning}
              type="warning"
              showIcon
              closable
              onClose={() => setDuplicateWarning('')}
              className="mb-6 border-l-4 border-yellow-500"
            />
          )}

          <Form.Item
            name="nomUsine"
            label={
              <Text strong className="text-gray-700">
                Nom de l'Usine
              </Text>
            }
            rules={[{ required: true, message: 'Veuillez sélectionner le nom de l\'usine' }]}
            help={
              !editingRecord && (
                <Text type="secondary" className="text-xs">
                  Les usines déjà existantes avec l'adresse par défaut sont masquées
                </Text>
              )
            }
          >
            <Select 
              placeholder="Sélectionnez une usine"
              onChange={handleUsineChange}
              showSearch
              optionFilterProp="children"
              disabled={loading}
              size="large"
              className="w-full"
              dropdownStyle={{ padding: 8 }}
            >
              {getFilteredUsineOptions().map(usine => (
                <Option key={usine.value} value={usine.value}>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-3"
                        style={{ backgroundColor: usine.color }}
                      />
                      <span className="font-medium">{usine.label}</span>
                    </div>
                    <Text type="secondary" className="text-xs">
                      {usine.adresse}
                    </Text>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="adresse"
            label={
              <Text strong className="text-gray-700">
                Adresse
              </Text>
            }
            extra={
              <Text type="secondary" className="text-xs mt-1">
                L'adresse se remplit automatiquement selon l'usine sélectionnée
              </Text>
            }
            rules={[
              { required: true, message: 'Veuillez saisir l\'adresse' },
              {
                validator: (_, value) => {
                  const nomUsine = form.getFieldValue('nomUsine');
                  if (nomUsine && value && checkForDuplicate(nomUsine, value)) {
                    return Promise.reject(new Error('Ce fournisseur existe déjà'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input.TextArea 
              placeholder="Adresse du fournisseur..."
              rows={3}
              size="large"
              onChange={handleAdresseChange}
              disabled={loading}
              className="w-full"
              style={{ resize: 'vertical' }}
            />
          </Form.Item>

          <div className="pt-6 border-t mt-8">
            <div className="flex justify-end gap-3">
              <Button 
                onClick={handleCancel} 
                disabled={loading}
                size="large"
                className="px-6"
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                disabled={!!duplicateWarning}
                size="large"
                className="px-6"
                icon={editingRecord ? <EditOutlined /> : <ArrowRightOutlined />}
              >
                {editingRecord ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* CSS personnalisé */}
      <style jsx>{`
        .ant-table-striped :global(.ant-table-tbody tr:nth-child(even)) {
          background-color: #fafafa;
        }
        
        .ant-table-striped :global(.ant-table-tbody tr:hover) {
          background-color: #f0f7ff !important;
        }
        
        .ant-pagination-custom :global(.ant-pagination-item-active) {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: transparent;
        }
        
        .ant-pagination-custom :global(.ant-pagination-item-active a) {
          color: white !important;
        }
        
        .modern-modal :global(.ant-modal-header) {
          border-bottom: 1px solid #f0f0f0;
          padding: 24px;
        }
        
        .modern-modal :global(.ant-modal-body) {
          padding: 24px;
        }
        
        .modern-modal :global(.ant-modal-footer) {
          border-top: 1px solid #f0f0f0;
          padding: 16px 24px;
        }
      `}</style>
    </div>
  );
};

export default FournisseursPage;